<script lang="ts">
import { computed, defineComponent, ref, toRefs, watch } from 'vue'

import PrimeButton from 'primevue/button'
import PrimeTextarea from 'primevue/textarea'

import { Expand } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import EmojiPicker from '@/components/atoms/emoji/EmojiPicker.vue'
import TextAIAssistant from '@/components/organism/textAiAssistant/TextAIAssistant.vue'
// import PreviewPhoneMessage from '@/pages/bot/components/PreviewPhoneMessage.vue'

export default defineComponent({
  name: 'AppEditorMessage',
  components: {
    PrimeTextarea,
    PrimeButton,
    AppDialog,
    EmojiPicker,
    TextAIAssistant,
    // PreviewPhoneMessage,
    Expand,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    maxlength: {
      type: Number,
      default: null,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    showErrorMessage: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: '',
    },
    showPreview: {
      type: Boolean,
      default: true,
    },
    showToolbar: {
      type: Boolean,
      default: true,
    },
    buttons: {
      type: Array as () => Array<'bold' | 'italic' | 'underline' | 'code' | 'bullet' | 'quote'>,
      default: () => ['bold', 'italic', 'underline', 'code', 'bullet', 'quote'],
    },
    instanceId: {
      type: String,
      default: '',
    },
    rows: {
      type: Number,
      default: 6,
    },
    textDialog: {
      type: String,
      default: 'Escribe tu mensaje',
    },
    textEditor: {
      type: String,
      default: 'Editor',
    },
    position: {
      type: String as () => 'left' | 'top' | 'bottom' | 'right',
      default: 'right',
    },
    maxEmojis: {
      type: Number,
      default: 0,
    },
    aiAttach: {
      type: Boolean,
      default: true,
    },
    aiEngine: {
      type: String as () => 'mistral' | string,
      default: 'mistral',
    },
    aiInsertMode: {
      type: String as () => 'replace' | 'append',
      default: 'replace',
    },
    compact: {
      type: Boolean,
      default: false,
    },
    enableVariables: {
      type: Boolean,
      default: false,
    },
    variableExamples: {
      type: Array as () => string[],
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'update:variableExamples', 'fullscreen-change'],
  setup(props, { emit }) {
    const isModalOpen = ref(false)
    const isTextareaResizable = ref(false)
    const { modelValue, instanceId, variableExamples } = toRefs(props)
    const internalValue = ref(modelValue.value)
    const internalVariableExamples = ref<string[]>([...variableExamples.value])
    const { t } = useI18n()
    const variablePattern = /\{\{(\d+)\}\}/g

    const resolvedPlaceholder = computed(() => props.placeholder || t('general.write_your_message'))
    // Genera dos IDs únicos, uno para cada textarea
    const mainTextareaId = computed(
      () =>
        `editor-textarea-main-${instanceId.value || Math.random().toString(36).substring(2, 9)}`,
    )
    const modalTextareaId = computed(
      () =>
        `editor-textarea-modal-${instanceId.value || Math.random().toString(36).substring(2, 9)}`,
    )

    // Devuelve el textarea activo según el modo
    const getActiveTextarea = () => {
      const id = isModalOpen.value ? modalTextareaId.value : mainTextareaId.value
      return document.getElementById(id) as HTMLTextAreaElement
    }

    const arraysAreEqual = (left: string[], right: string[]) =>
      left.length === right.length && left.every((value, index) => value === right[index])

    const sanitizeVariableExample = (value: string) => value.replace(/[{}]/g, '').trim()

    const getVariableMatches = (content: string) =>
      Array.from(content.matchAll(variablePattern)).map((match) => ({
        raw: match[0],
        originalIndex: Number(match[1]),
      }))

    const normalizeVariableState = (content: string, examples: string[]) => {
      const matches = getVariableMatches(content)
      const tokenOrder = new Map<number, number>()

      const normalizedContent = content.replace(variablePattern, (_match, indexValue: string) => {
        const originalIndex = Number(indexValue)

        if (!tokenOrder.has(originalIndex)) {
          tokenOrder.set(originalIndex, tokenOrder.size + 1)
        }

        return `{{${tokenOrder.get(originalIndex)}}}`
      })

      const normalizedExamples = Array.from(tokenOrder.entries())
        .sort((left, right) => left[1] - right[1])
        .map(([originalIndex]) => sanitizeVariableExample(examples[originalIndex - 1] ?? ''))

      return {
        content: normalizedContent,
        examples: normalizedExamples,
        count: matches.length ? tokenOrder.size : 0,
      }
    }

    const getExactVariableCount = (content: string) => normalizeVariableState(content, []).count

    const emitEditorState = (content: string, examples: string[]) => {
      const examplesChanged = !arraysAreEqual(examples, props.variableExamples)

      if (content !== props.modelValue || (props.enableVariables && examplesChanged)) {
        emit('update:modelValue', content)
      }

      if (examplesChanged) {
        emit('update:variableExamples', examples)
      }
    }

    const applyEditorState = (content: string, examples = internalVariableExamples.value) => {
      if (!props.enableVariables) {
        internalValue.value = content
        emitEditorState(content, [])
        return
      }

      const normalized = normalizeVariableState(String(content ?? ''), [...examples])
      internalValue.value = normalized.content
      internalVariableExamples.value = normalized.examples
      emitEditorState(normalized.content, normalized.examples)
    }

    watch(
      () => props.modelValue,
      (value) => {
        if (!props.enableVariables) {
          if (value !== internalValue.value) internalValue.value = value
          return
        }

        const normalized = normalizeVariableState(String(value ?? ''), [
          ...internalVariableExamples.value,
        ])

        if (normalized.content !== internalValue.value) {
          internalValue.value = normalized.content
        }

        if (!arraysAreEqual(normalized.examples, internalVariableExamples.value)) {
          internalVariableExamples.value = normalized.examples
        }
      },
    )

    watch(
      () => props.variableExamples,
      (value) => {
        if (!props.enableVariables) return
        if (arraysAreEqual(value, internalVariableExamples.value)) return

        const normalized = normalizeVariableState(String(internalValue.value ?? ''), [...value])
        internalVariableExamples.value = normalized.examples

        if (normalized.content !== internalValue.value) {
          internalValue.value = normalized.content
        }
      },
      { deep: true },
    )

    const updateContent = (value: string) => {
      applyEditorState(String(value ?? ''), internalVariableExamples.value)
    }

    const replaceWithAiResult = (value: string) => {
      updateContent(String(value ?? ''))
    }

    const appendAiResult = (value: string) => {
      const result = String(value ?? '').trim()
      if (!result) return
      updateContent(`${internalValue.value}${internalValue.value ? '\n' : ''}${result}`)
    }

    // Inserta markdown multilínea para todos los botones
    const insertMultilineTag = (open: string, close: string) => {
      const textarea = getActiveTextarea()
      if (!textarea) return

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selected = internalValue.value.slice(start, end)
      let newText = selected

      // Si hay salto de línea, aplicar a cada línea
      if (selected.includes('\n')) {
        newText = selected
          .split('\n')
          .map((line) => (line ? `${open}${line}${close}` : ''))
          .join('\n')
      } else {
        newText = `${open}${selected}${close}`
      }

      const before = internalValue.value.slice(0, start)
      const after = internalValue.value.slice(end)
      applyEditorState(`${before}${newText}${after}`)

      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + open.length, start + newText.length - close.length)
      }, 0)
    }

    const insertBulletList = () => {
      const textarea = getActiveTextarea()
      if (!textarea) return

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selected = internalValue.value.slice(start, end)
      const lines = selected.split('\n').map((line) => (line ? `* ${line}` : '*'))
      const newText = lines.join('\n')

      const before = internalValue.value.slice(0, start)
      const after = internalValue.value.slice(end)
      applyEditorState(`${before}${newText}${after}`)

      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start, start + newText.length)
      }, 0)
    }

    const insertQuote = () => {
      const textarea = getActiveTextarea()
      if (!textarea) return

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selected = internalValue.value.slice(start, end)
      const lines = selected.split('\n').map((line) => (line ? `> ${line}` : '>'))
      const newText = lines.join('\n')

      const before = internalValue.value.slice(0, start)
      const after = internalValue.value.slice(end)
      applyEditorState(`${before}${newText}${after}`)

      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start, start + newText.length)
      }, 0)
    }

    const insertCodeBlock = () => {
      const textarea = getActiveTextarea()
      if (!textarea) return

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selected = internalValue.value.slice(start, end)
      let newText = selected

      // Si hay salto de línea o selección vacía, usar bloque de código
      if (selected.includes('\n') || selected === '') {
        newText = `\`\`\`\n${selected}\n\`\`\``
      } else {
        // Si es una sola línea, usar bloque inline
        newText = `\`${selected}\``
      }

      const before = internalValue.value.slice(0, start)
      const after = internalValue.value.slice(end)
      applyEditorState(`${before}${newText}${after}`)

      setTimeout(() => {
        textarea.focus()
        // Selecciona el texto dentro del bloque de código
        if (selected.includes('\n') || selected === '') {
          textarea.setSelectionRange(start + 4, start + 4 + selected.length)
        } else {
          textarea.setSelectionRange(start + 1, start + 1 + selected.length)
        }
      }, 0)
    }

    const onEmojiSelected = (emoji: string) => {
      if (props.maxEmojis != 0 && emojiCount.value >= props.maxEmojis) {
        return
      }
      const textarea = getActiveTextarea()
      if (!textarea) return

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const before = internalValue.value.slice(0, start)
      const after = internalValue.value.slice(end)
      applyEditorState(`${before}${emoji}${after}`)

      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + emoji.length, start + emoji.length)
      }, 0)
    }

    const activeVariableTokens = computed(() =>
      Array.from(
        { length: getExactVariableCount(String(internalValue.value ?? '')) },
        (_, index) => `{{${index + 1}}}`,
      ),
    )

    const getVariableInsertionText = (content: string, start: number, token: string) => {
      const previousCharacter = content.slice(0, start).slice(-1)
      const needsLeadingSpace =
        start > 0 && Boolean(previousCharacter) && !/\s/.test(previousCharacter)
      return `${needsLeadingSpace ? ' ' : ''}${token}`
    }

    const variableBoundaryErrorMessage = computed(() => {
      if (!props.enableVariables) return ''

      const content = String(internalValue.value ?? '')
      if (!content.trim()) return ''

      const startsWithVariable = /^\s*\{\{\d+\}\}/.test(content)
      const endsWithVariable = /\{\{\d+\}\}\s*$/.test(content)

      return startsWithVariable || endsWithVariable ? t('template.variables.boundary_error') : ''
    })

    const effectiveErrorMessage = computed(() => {
      if (!props.showErrorMessage) return ''
      return props.errorMessage || variableBoundaryErrorMessage.value
    })

    const canAddVariable = computed(() => {
      if (!props.enableVariables) return false
      const nextToken = `{{${activeVariableTokens.value.length + 1}}}`
      return !props.maxlength || internalValue.value.length + nextToken.length <= props.maxlength
    })

    const updateVariableExample = (index: number, value: string) => {
      const nextExamples = [...internalVariableExamples.value]
      nextExamples[index] = sanitizeVariableExample(value)
      applyEditorState(internalValue.value, nextExamples)
    }

    const addVariable = () => {
      if (!props.enableVariables || !canAddVariable.value) return

      const textarea = getActiveTextarea()
      if (!textarea) return

      const nextVariableIndex = activeVariableTokens.value.length + 1
      const nextToken = `{{${nextVariableIndex}}}`
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const content = String(internalValue.value ?? '')
      const before = internalValue.value.slice(0, start)
      const after = internalValue.value.slice(end)
      const insertionText = getVariableInsertionText(content, start, nextToken)
      const nextContent = `${before}${insertionText}${after}`

      if (props.maxlength && nextContent.length > props.maxlength) return

      const nextExamples = [...internalVariableExamples.value]
      nextExamples[nextVariableIndex - 1] = nextExamples[nextVariableIndex - 1] ?? ''
      applyEditorState(nextContent, nextExamples)

      setTimeout(() => {
        textarea.focus()
        const nextCursorPosition = start + insertionText.length
        textarea.setSelectionRange(nextCursorPosition, nextCursorPosition)
      }, 0)
    }

    const removeVariableToken = (
      textarea: HTMLTextAreaElement,
      tokenStart: number,
      tokenEnd: number,
    ) => {
      applyEditorState(
        `${internalValue.value.slice(0, tokenStart)}${internalValue.value.slice(tokenEnd)}`,
      )

      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(tokenStart, tokenStart)
      }, 0)
    }

    const onTextareaKeydown = (event: KeyboardEvent) => {
      if (!props.enableVariables) return
      if (event.key !== 'Backspace' && event.key !== 'Delete') return

      const textarea = event.target as HTMLTextAreaElement | null
      if (!textarea) return

      const content = String(internalValue.value ?? '')
      const selectionStart = textarea.selectionStart ?? 0
      const selectionEnd = textarea.selectionEnd ?? 0
      const matches = Array.from(content.matchAll(variablePattern))

      for (const match of matches) {
        const token = match[0]
        const tokenStart = match.index ?? -1
        if (tokenStart === -1) continue
        const tokenEnd = tokenStart + token.length

        if (selectionStart !== selectionEnd) {
          const selectionTouchesToken = selectionStart < tokenEnd && selectionEnd > tokenStart
          if (!selectionTouchesToken) continue

          event.preventDefault()
          removeVariableToken(textarea, tokenStart, tokenEnd)
          return
        }

        const isBackspaceOnEdge = event.key === 'Backspace' && selectionStart === tokenEnd
        const isDeleteOnEdge = event.key === 'Delete' && selectionStart === tokenStart
        const isCursorInsideToken = selectionStart > tokenStart && selectionStart < tokenEnd

        if (!isBackspaceOnEdge && !isDeleteOnEdge && !isCursorInsideToken) continue

        event.preventDefault()
        removeVariableToken(textarea, tokenStart, tokenEnd)
        return
      }
    }

    const previewContent = computed(() => {
      if (!props.enableVariables) return internalValue.value

      return activeVariableTokens.value.reduce((content, token, index) => {
        const example = sanitizeVariableExample(internalVariableExamples.value[index] ?? '')
        return content.replace(new RegExp(token.replace(/[{}]/g, '\\$&'), 'g'), example)
      }, internalValue.value)
    })

    const buttonActions = {
      bold: () => insertMultilineTag('**', '**'),
      italic: () => insertMultilineTag('*', '*'),
      underline: () => insertMultilineTag('__', '__'),
      code: insertCodeBlock,
      bullet: insertBulletList,
      quote: insertQuote,
    }

    const buttonLabels = {
      bold: 'B',
      italic: 'I',
      underline: 'U',
      code: '</>',
      bullet: '•',
      quote: '""',
    }

    const buttonIcons: Partial<
      Record<'bold' | 'italic' | 'underline' | 'code' | 'bullet' | 'quote', string>
    > = {
      code: 'pi pi-code',
    }

    const openModal = () => {
      isModalOpen.value = true
      isTextareaResizable.value = true
      emit('fullscreen-change', true)
    }

    const closeModal = () => {
      isModalOpen.value = false
      isTextareaResizable.value = false
      emit('fullscreen-change', false)
    }

    const emojiCount = computed(() => {
      const match = internalValue.value.match(/\p{Emoji_Presentation}|\p{Emoji}\uFE0F/gu)
      return match ? match.length : 0
    })

    return {
      internalValue,
      mainTextareaId,
      modalTextareaId,
      buttonActions,
      buttonLabels,
      buttonIcons,
      isModalOpen,
      isTextareaResizable,
      openModal,
      closeModal,
      onEmojiSelected,
      emojiCount,
      resolvedPlaceholder,
      activeVariableTokens,
      internalVariableExamples,
      canAddVariable,
      updateVariableExample,
      addVariable,
      onTextareaKeydown,
      previewContent,
      updateContent,
      replaceWithAiResult,
      appendAiResult,
      variableBoundaryErrorMessage,
      effectiveErrorMessage,
    }
  },
})
</script>

<template>
  <div class="w-full">
    <div class="pb-2">
      <div class="flex flex-row gap-6 w-full items-start">
        <div class="flex-1 flex flex-col min-w-0">
          <div
            v-if="showToolbar"
            class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 dark:bg-[var(--p-surface-900)] border border-[var(--p-surface-200)] dark:border-[var(--p-surface-700)] rounded-t-lg border-b-0"
          >
            <div class="flex items-center gap-2">
              <EmojiPicker
                :position="position"
                @emoji="onEmojiSelected"
                class="transition-all duration-200 hover:scale-105"
              />

              <TextAIAssistant
                v-if="aiAttach"
                :model-value="String(internalValue ?? '')"
                field-type="textarea"
                context-label="mensaje"
                panel-mode="fixed"
                panel-placement="right"
                :panel-z-index="20000"
                :embedded="false"
                :allow-append="true"
                @replace="replaceWithAiResult"
                @append="appendAiResult"
                class="transition-all duration-200 hover:scale-105"
              />

              <!-- <PrimeButton
              v-tooltip.top="'Editor de pantalla completa'"
              @click="openModal"
              class="w-7 h-7 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-all duration-200 border-0 shadow-sm hover:shadow-md"
              icon="pi pi-window-maximize"
              text
            /> -->
            </div>

            <div class="flex items-center gap-1.5 flex-wrap">
              <button
                v-if="enableVariables"
                type="button"
                class="rounded-full cursor-pointer shadow-sm px-3 py-1 text-xs font-medium dark:bg-gray-800 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:text-white"
                :disabled="!canAddVariable"
                @click="addVariable"
              >
                {{ $t('template.variables.add_variable') }}
              </button>
              <span
                v-if="maxlength"
                class="monstserrat text-[10px] shadow-sm px-2 py-1 bg-white-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg dark:border-gray-700"
              >
                {{ internalValue.length }} / {{ maxlength }}
              </span>
              <PrimeButton
                v-for="button in buttons"
                :key="button"
                v-tooltip.top="button"
                @click="buttonActions[button]()"
                class="w-7 h-7 rounded-lg transition-all duration-200 border shadow-sm hover:shadow-md"
                :class="{
                  'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 border-blue-200 dark:border-blue-800':
                    ['bold', 'italic', 'underline'].includes(button),
                  'bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900 border-green-200 dark:border-green-800':
                    button === 'code',
                  'bg-purple-50 dark:bg-purple-950 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900 border-purple-200 dark:border-purple-800':
                    ['bullet', 'quote'].includes(button),
                }"
                :icon="buttonIcons[button]"
                text
              >
                <template v-if="!buttonIcons[button]">
                  <span
                    class="font-semibold"
                    :class="{
                      'font-bold': button === 'bold',
                      italic: button === 'italic',
                      underline: button === 'underline',
                    }"
                  >
                    {{ buttonLabels[button] }}
                  </span>
                </template>
              </PrimeButton>

              <button
                type="button"
                class="w-7 h-7 rounded-lg flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-300 dark:hover:bg-neutral-600 transition-all duration-200 hover:scale-105 cursor-pointer shadow-sm border border-neutral-300 dark:border-neutral-700"
                v-tooltip.top="'Editor de pantalla completa'"
                @click="openModal"
              >
                <Expand class="w-4 h-4 text-(--p-primary-color)" />
              </button>
            </div>
          </div>

          <div class="">
            <div class="flex">
              <PrimeTextarea
                :id="mainTextareaId"
                :modelValue="internalValue"
                @update:modelValue="updateContent"
                @keydown="onTextareaKeydown"
                :placeholder="resolvedPlaceholder"
                autocomplete="off"
                class="w-full h-full border border-neutral-200 dark:border-neutral-700 rounded-b-lg border-t-0 resize-none text-xs! 2xl:text-sm! leading-relaxed text-[var(--p-surface-900)] dark:text-[var(--p-surface-100)] placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-(--p-primary-color) !bg-[var(--color-inputs)]/5 dark:!bg-[var(--p-surface-800)]"
                :class="{
                  'mb-0 min-h-[56px] p-3': compact,
                  'mb-0 min-h-[120px] p-6': !compact,
                  'bg-red-50 dark:bg-red-950 border-red-300 dark:border-red-800 focus:ring-red-500':
                    effectiveErrorMessage,
                  'bg-amber-50 dark:bg-amber-950 border-amber-300 dark:border-amber-800':
                    maxEmojis > 0 && emojiCount >= maxEmojis,
                }"
                :maxlength="maxlength"
                :rows="rows"
                autoResize
              />
            </div>
            <!-- <small v-if="errorContent" class="text-red-500 text-xs">{{ errorContent }}</small> -->
            <small v-if="effectiveErrorMessage" class="text-red-400 text-xs"
              >{{ effectiveErrorMessage }}
            </small>
          </div>
          <!-- <AppPreviewCard
        v-if="showPreview"
        :content="internalValue"
        class="lg:max-w-md w-full lg:w-auto flex-1"
      /> -->
        </div>

        <div
          v-if="showPreview"
          class="dark:bg-neutral-900/60 backdrop-blur-sm pl-4 rounded-2xl flex justify-center min-h-[25rem] shrink-0 self-start"
        >
          <div class="w-full max-w-[280px] transform scale-90 lg:scale-100 transition-transform">
            <!-- <PreviewPhoneMessage :content="previewContent" /> -->
          </div>
        </div>
      </div>

      <div v-if="maxEmojis > 0" class="flex mt-2 justify-end">
        <div
          class="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors duration-200 border shadow-sm"
          :class="{
            'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800':
              emojiCount < maxEmojis,
            'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800':
              emojiCount >= maxEmojis,
          }"
        >
          <i
            class="pi"
            :class="{
              'pi-smile text-green-500 dark:text-green-400': emojiCount < maxEmojis,
              'pi-frown text-red-500 dark:text-red-400': emojiCount >= maxEmojis,
            }"
          ></i>
          <span class="text-[10px] font-medium">
            {{ emojiCount }}/{{ maxEmojis }} {{ $t('general.emojis') }}
          </span>
        </div>
      </div>
      <div v-if="enableVariables && activeVariableTokens.length" class="mt-3 mb-3 space-y-2">
        <label class="mb-2 block text-xs dark:text-slate-100"
          >{{ $t('template.variables.variable_name') }}
        </label>
        <div
          v-for="(token, index) in activeVariableTokens"
          :key="token"
          class="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-1 dark:border-slate-700"
        >
          <span
            class="rounded-full bg-[var(--p-primary-50)] px-2.5 py-1 text-sm font-medium text-[var(--p-primary-color)] dark:bg-white/10 dark:text-white"
          >
            {{ token }}
          </span>
          <input
            :value="internalVariableExamples[index] ?? ''"
            type="text"
            class="w-full bg-transparent text-xs text-slate-700 outline-none placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-500"
            :placeholder="$t('template.variables.value_example_for', { token: token })"
            @input="
              (event) => updateVariableExample(index, (event.target as HTMLInputElement).value)
            "
          />
        </div>
      </div>
    </div>

    <AppDialog
      v-model="isModalOpen"
      :header="textDialog"
      :closable="true"
      :maximizable="true"
      :modal="true"
      class="w-full max-w-7xl"
      @hide="closeModal"
    >
      <template #header>
        <div class="flex items-center gap-3">
          <i class="pi pi-pencil text-(--p-primary-color) text-xl"></i>
          <span class="text-xl font-semibold text-neutral-900 dark:text-neutral-100">{{
            textDialog
          }}</span>
        </div>
      </template>

      <template #content>
        <div class="flex flex-row gap-6 h-[70vh] items-start">
          <div
            class="flex-1 flex flex-col min-w-0 bg-white dark:bg-neutral-950 rounded-xl border border-neutral-200 dark:border-neutral-700 shadow-lg"
          >
            <div
              v-if="showToolbar"
              class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 bg-neutral-50 dark:bg-neutral-900 rounded-t-xl border-b border-neutral-200 dark:border-neutral-700"
            >
              <div class="flex items-center gap-2 flex-wrap">
                <button
                  v-if="enableVariables"
                  type="button"
                  class="rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 transition disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200"
                  :disabled="!canAddVariable"
                  @click="addVariable"
                >
                  Agregar variable
                </button>
                <PrimeButton
                  v-for="button in buttons"
                  :key="button"
                  v-tooltip.top="button"
                  @click="buttonActions[button]()"
                  class="w-8 h-8 rounded-xl transition-all duration-200 border shadow-sm hover:shadow-md"
                  :class="{
                    'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900 border-blue-200 dark:border-blue-800':
                      ['bold', 'italic', 'underline'].includes(button),
                    'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900 border-green-200 dark:border-green-800':
                      button === 'code',
                    'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900 border-purple-200 dark:border-purple-800':
                      ['bullet', 'quote'].includes(button),
                  }"
                  :icon="buttonIcons[button]"
                  text
                >
                  <template v-if="!buttonIcons[button]">
                    <span
                      class="font-semibold text-lg"
                      :class="{
                        'font-bold': button === 'bold',
                        italic: button === 'italic',
                        underline: button === 'underline',
                      }"
                    >
                      {{ buttonLabels[button] }}
                    </span>
                  </template>
                </PrimeButton>
                <span
                  v-if="maxlength"
                  class="text-xs font-medium px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  {{ internalValue.length }} / {{ maxlength }}
                </span>
              </div>

              <div class="flex items-center gap-3">
                <EmojiPicker
                  :position="position"
                  @emoji="onEmojiSelected"
                  class="transition-all duration-200 hover:scale-110"
                />

                <TextAIAssistant
                  v-if="aiAttach"
                  :model-value="String(internalValue ?? '')"
                  field-type="textarea"
                  context-label="mensaje"
                  panel-mode="fixed"
                  panel-placement="right"
                  :panel-z-index="20000"
                  :embedded="false"
                  :allow-append="true"
                  @replace="replaceWithAiResult"
                  @append="appendAiResult"
                  class="transition-all duration-200 hover:scale-110"
                />
              </div>
            </div>

            <div class="flex-1 p-6 bg-white dark:bg-neutral-950 rounded-b-xl">
              <PrimeTextarea
                :id="modalTextareaId"
                :modelValue="internalValue"
                @update:modelValue="updateContent"
                @keydown="onTextareaKeydown"
                autocomplete="off"
                class="w-full h-full min-h-[300px] p-5 border border-neutral-200 dark:border-neutral-700 rounded-lg resize-none text-base leading-relaxed text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-(--p-primary-color) focus:border-(--p-primary-color) transition-all duration-200 bg-white dark:bg-neutral-900"
                :class="{
                  'bg-red-50 dark:bg-red-950 border-red-300 dark:border-red-800 focus:ring-red-500':
                    effectiveErrorMessage,
                  'bg-amber-50 dark:bg-amber-950 border-amber-300 dark:border-amber-800':
                    maxEmojis > 0 && emojiCount >= maxEmojis,
                }"
                :placeholder="placeholder"
                :maxlength="maxlength"
                :rows="rows"
                autoResize
              />

              <div v-if="enableVariables && activeVariableTokens.length" class="mt-4 space-y-2">
                <div
                  v-for="(token, index) in activeVariableTokens"
                  :key="`modal-${token}`"
                  class="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-700"
                >
                  <span
                    class="rounded-full bg-[var(--p-primary-50)] px-2.5 py-1 text-xs font-semibold text-[var(--p-primary-color)] dark:bg-white/10 dark:text-white"
                  >
                    {{ token }}
                  </span>
                  <input
                    :value="internalVariableExamples[index] ?? ''"
                    type="text"
                    class="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-500"
                    :placeholder="`Ejemplo para ${token}`"
                    @input="
                      (event) =>
                        updateVariableExample(index, (event.target as HTMLInputElement).value)
                    "
                  />
                </div>
              </div>
            </div>

            <div
              v-if="effectiveErrorMessage"
              class="flex items-center gap-3 px-6 py-4 bg-red-50 dark:bg-red-950 border-t border-red-200 dark:border-red-800 rounded-b-xl"
            >
              <i class="pi pi-exclamation-circle text-red-500 dark:text-red-400 text-lg"></i>
              <span class="text-sm font-medium text-red-700 dark:text-red-400">{{
                effectiveErrorMessage
              }}</span>
            </div>
          </div>

          <!-- <AppPreviewCard
            :content="internalValue"
            maxHeight="100"
            class="lg:max-w-md w-full lg:w-auto flex-1"
          /> -->

          <div
            v-if="showPreview"
            class="dark:bg-neutral-900/60 backdrop-blur-sm pl-4 rounded-2xl flex justify-center min-h-[25rem] shrink-0 self-start mt-12"
          >
            <div class="w-full max-w-[280px] transform scale-100 lg:scale-120 transition-transform">
              <!-- <PreviewPhoneMessage :content="previewContent" /> -->
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex items-center justify-between w-full">
          <div
            v-if="maxEmojis > 0"
            class="flex items-center gap-2 px-4 py-2 rounded-lg border shadow-sm"
            :class="{
              'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800':
                emojiCount < maxEmojis,
              'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800':
                emojiCount >= maxEmojis,
            }"
          >
            <i
              class="pi"
              :class="{
                'pi-smile text-green-600 dark:text-green-400': emojiCount < maxEmojis,
                'pi-frown text-red-600 dark:text-red-400': emojiCount >= maxEmojis,
              }"
            ></i>
            <span class="text-sm font-medium"> {{ emojiCount }}/{{ maxEmojis }} emojis </span>
          </div>

          <div class="flex items-center gap-3 ml-auto">
            <PrimeButton
              label="Cancelar"
              @click="closeModal"
              class="px-6 py-2.5 text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors duration-200 border border-neutral-300 dark:border-neutral-700 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
              text
            />
            <PrimeButton
              label="Aplicar"
              @click="closeModal"
              class="px-6 py-2.5 bg-(--p-primary-color) text-white hover:opacity-90 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            />
          </div>
        </div>
      </template>
    </AppDialog>
  </div>
</template>

<style scoped>
.p-textarea {
  border-radius: 0.5rem !important;
  font-size: 1rem !important;
  line-height: 1.6 !important;
}

.p-dialog .p-dialog-header {
  border-bottom: 1px solid #e5e7eb !important;
}

:global(.dark) .p-dialog .p-dialog-header {
  border-bottom: 1px solid #374151 !important;
}

.p-dialog .p-dialog-content {
  padding: 1.5rem !important;
}

.p-button,
.emoji-picker,
.ai-generate {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.p-button:hover {
  transform: translateY(-1px) !important;
}

.p-textarea::-webkit-scrollbar {
  width: 8px;
}

.p-textarea::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 4px;
}

:global(.dark) .p-textarea::-webkit-scrollbar-track {
  background: #1f2937;
}

.p-textarea::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

:global(.dark) .p-textarea::-webkit-scrollbar-thumb {
  background: #4b5563;
}

.p-textarea::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

:global(.dark) .p-textarea::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.p-button:focus-visible {
  outline: 2px solid var(--p-primary-color) !important;
  outline-offset: 2px !important;
}

.p-textarea:focus {
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--p-primary-color) 20%, transparent) !important;
}
</style>
